import { useEffect, useState, useRef } from "react";
import { AlarmClock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import RemindersPanel from "@/components/RemindersPanel";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

/**
 * Header icon that opens the floating Reminders panel and shows a badge
 * with the count of reminders that have fired (due) for the current user.
 */
export default function RemindersBell() {
  const { activeWorkspaceId } = useAuth();
  const [open, setOpen] = useState(false);
  const [due, setDue] = useState(0);
  const pollRef = useRef(null);

  const load = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/reminders/unread-count`);
      setDue(data?.due || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    // Poll every 60s. Cheap endpoint; also refreshes when panel closes.
    // Feb 2026 R281 · Visibility-gated so background tabs stop hitting.
    const stopPoll = pollWhenVisible(load, 60_000);
    const onChanged = () => load();
    window.addEventListener("reminders:changed", onChanged);
    return () => {
      stopPoll();
      window.removeEventListener("reminders:changed", onChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId]);

  // Re-fetch badge when panel closes (user may have completed some)
  useEffect(() => {
    if (!open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-1.5 text-muted-foreground hover:text-foreground transition"
        aria-label="Reminders"
        data-testid="reminders-bell-btn"
      >
        <AlarmClock className="w-4 h-4" />
        {due > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold leading-none flex items-center justify-center"
            data-testid="reminders-badge"
          >
            {due > 99 ? "99+" : due}
          </span>
        )}
      </button>
      <RemindersPanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}
